import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'routes';

import ProfileAvatar from 'modules/users/components/ProfileAvatar';
import type {
  CaseStudy,
  CaseStudyPhotoWithLocalizedTexts,
} from 'modules/marketing-pages/marketing-page.types';

import Expandable from '../Expandable';

import styles from './index.module.scss';
import CaseStudyPhotos from 'components/molecules/CaseStudyPhotos';
import classNames from 'classnames';

const NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW = 5;
const INFO_LIST_LINE_HEIGHT = 40;
const INFO_LIST_SHOW_MORE_MARGIN = 30;
const INFO_LIST_MIN_HEIGHT =
  NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW * INFO_LIST_LINE_HEIGHT + INFO_LIST_SHOW_MORE_MARGIN;
const INFO_MESSAGE_LENGTH: Record<string, number> = {
  en: 125,
  ja: 61,
};

const SUMMARY_MIN_HEIGHT = 72;

const MESSAGE_FROM_PRO_LENGTH: Record<string, number> = {
  en: 200,
  ja: 90,
};
const MESSAGE_FROM_PRO_MIN_HEIGHT = 143;

const THUMBNAIL_HEIGHT = 100;

const PHOTOS_PER_ROW = 2;

// NOTE: Takahiro Shigemoto
// For personal trainers, do not display the second line. Note that this is temporary.
const shouldDisplayBasicInfo = (jobtypeId: string, index: number) =>
  jobtypeId !== 'personal-trainer' || index !== 1;

export interface Props {
  caseStudy: CaseStudy;
}

const CaseStudyCard: FC<Props> = ({
  caseStudy: {
    title,
    summary,
    proMessage,
    photos,
    proPhotoUrl,
    basicInfoList = [],
    displayName,
    jobTypeId,
    userId,
    slug,
  },
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation('marketingPages');

  const infoListExpanded = useMemo(
    () => basicInfoList.length <= NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW,
    [basicInfoList]
  );

  let proLink = '';
  if (Boolean(slug) || Boolean(userId)) {
    proLink = `${language === 'en' ? '/en' : ''}/profile/${slug ?? userId ?? ''}/pro/${jobTypeId}`;
  }

  const photosWithLocalizedCaptions = useMemo(
    () =>
      photos?.reduce((allPhotos, photo) => {
        if (photo.photoUrl) {
          return allPhotos.concat({
            altText: photo.altText,
            description: photo.description,
            url: photo.photoUrl,
          });
        }
        return allPhotos;
      }, [] as Array<CaseStudyPhotoWithLocalizedTexts>),
    [photos]
  );

  return (
    <div className={styles.container} data-test-id="case-study-card">
      <div className={styles.title}>{title}</div>
      <Expandable
        className={styles.summary}
        defaultExpanded={(summary?.length ?? 0) <= INFO_MESSAGE_LENGTH[language]}
        expandText={t('readMore')}
        expandClassname={classNames(styles.more, styles['bottom-right'])}
        minHeight={SUMMARY_MIN_HEIGHT}
        hasIcon={false}
      >
        <pre className={styles['molded-sentences']}>{summary ?? ''}</pre>
      </Expandable>
      <div className={styles.hr} />
      <div className={styles['pro-container']}>
        <div className={styles['avatar-container']}>
          {proLink && (
            <Link prefetch={false} route={proLink}>
              <a>
                <ProfileAvatar name={displayName} avatar={proPhotoUrl} />
              </a>
            </Link>
          )}
          {!proLink && <ProfileAvatar name={displayName} avatar={proPhotoUrl} />}
          <div className={styles['pro-name']}>{displayName}</div>
        </div>
        <div className={styles['pro-message']}>
          <Expandable
            defaultExpanded={(proMessage?.length ?? 0) <= MESSAGE_FROM_PRO_LENGTH[language]}
            expandText={t('readMore')}
            expandClassname={classNames(styles.more, styles['bottom-right'])}
            minHeight={MESSAGE_FROM_PRO_MIN_HEIGHT}
            hasIcon={false}
          >
            <pre className={styles['molded-sentences']}>{proMessage ?? ''}</pre>
          </Expandable>
        </div>
      </div>
      <CaseStudyPhotos
        rowLimit={PHOTOS_PER_ROW}
        thumbnailHeight={THUMBNAIL_HEIGHT}
        photos={photosWithLocalizedCaptions}
      />
      <Expandable
        defaultExpanded={infoListExpanded}
        minHeight={INFO_LIST_MIN_HEIGHT}
        title={t('basicInfo')}
        expandClassname={classNames(styles.more, styles['bottom-right'], styles['full-width'])}
      >
        <div className={styles['basic-info-table']}>
          {basicInfoList.map(
            (basicInfo, index) =>
              shouldDisplayBasicInfo(jobTypeId, index) && (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`basic-info-${index}`}>
                  <div
                    className={classNames(
                      styles['basic-info-table-cell'],
                      styles['basic-info-label']
                    )}
                  >
                    {basicInfo.title}
                  </div>
                  <div
                    className={classNames(
                      styles['basic-info-table-cell'],
                      styles['basic-info-value']
                    )}
                  >
                    {basicInfo.description}
                  </div>
                </div>
              )
          )}
        </div>
      </Expandable>
    </div>
  );
};

export default CaseStudyCard;
export type { Props as CaseStudyCardProps };
